/* eslint-disable complexity -- This screen renders explicit workflow states and audited job evidence. */
import { useState } from "react"
import type { StoreCode } from "@nova-ai/shared"
import {
  galleryStudioSlotNameSchema,
  galleryStudioSlotPlanConflictDetailsSchema,
  isGalleryStudioPlanSlotReady,
} from "@nova-ai/shared/gallery-studio"
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  CircleDollarSign,
  FlaskConical,
  Images,
  Layers3,
  LoaderCircle,
  LockKeyhole,
  Plus,
  Play,
  Square,
  StopCircle,
} from "lucide-react"

import { useGalleryStudioWorkspace } from "../../hooks/use-gallery-studio-workspace"
import {
  useGalleryStudioPlanInputs,
  useGalleryStudioBootstrapInputs,
  useCreateGalleryStudioIntent,
  GalleryStudioApiError,
  type GalleryDraftIdentity,
  type GalleryDraftJob,
  type GalleryDraftPreview,
  type GalleryStudioPlanInputs,
  type GalleryStudioPlanSlot,
} from "../../services/use-gallery-studio"
import { cn } from "../../lib/utils"
import { Button } from "../ui/button"
import { Callout } from "../ui/callout"
import { StatusBadge, type BadgeTone } from "../ui/status-badge"
import { FormField } from "../ui/form-field"
import { TextInput } from "../ui/text-input"
import {
  GalleryStudioBootstrapState,
  isMissingGalleryStudioIntent,
} from "./gallery-studio-bootstrap"
import { GalleryStudioLoading } from "./gallery-studio-loading"
import { GalleryStudioSlotSection } from "./gallery-studio-slot-section"

export function GalleryStudioView({ configSku, store }: { configSku: string; store: StoreCode }) {
  const inputs = useGalleryStudioPlanInputs(configSku, store)
  const isMissingIntent = isMissingGalleryStudioIntent(inputs.error)
  const bootstrap = useGalleryStudioBootstrapInputs(configSku, store, isMissingIntent)
  const createIntent = useCreateGalleryStudioIntent(configSku)

  return (
    <section className="space-y-5">
      <header>
        <div className="max-w-2xl">
          <h2 className="text-[15px] font-semibold tracking-tight">Gallery Studio</h2>

          <p className="mt-1 text-xs text-muted-foreground">
            Plan complete, unselected gallery drafts. Main stays locked. Review exact paid work
            before launch.
          </p>
        </div>
      </header>

      {inputs.isLoading ? <GalleryStudioLoading /> : null}

      {isMissingIntent ? (
        <GalleryStudioBootstrapState
          inputs={bootstrap.data}
          isLoading={bootstrap.isLoading}
          error={bootstrap.error}
          createError={createIntent.error}
          isCreating={createIntent.isPending}
          onRetry={() => void bootstrap.refetch()}
          onCreate={(targetSimpleSkus) => createIntent.mutateAsync(targetSimpleSkus)}
          onOpenPersisted={() => void inputs.refetch()}
        />
      ) : null}

      {inputs.error && !inputs.data && !isMissingIntent ? (
        <Callout
          role="alert"
          tone="destructive"
          icon={<AlertCircle className="size-4" />}
          title="Gallery intent could not load"
          action={
            <Button size="sm" onClick={() => void inputs.refetch()}>
              Retry
            </Button>
          }
        >
          <p className="mt-2 text-xs text-destructive">{inputs.error.message}</p>
        </Callout>
      ) : null}

      {inputs.data ? (
        <GalleryStudioWorkspace
          inputs={inputs.data}
          hasMoreDrafts={inputs.hasNextPage}
          isLoadingMoreDrafts={inputs.isFetchingNextPage}
          onLoadMoreDrafts={() => void inputs.fetchNextPage()}
        />
      ) : null}
    </section>
  )
}

function GalleryStudioWorkspace({
  inputs,
  hasMoreDrafts,
  isLoadingMoreDrafts,
  onLoadMoreDrafts,
}: {
  inputs: GalleryStudioPlanInputs
  hasMoreDrafts: boolean
  isLoadingMoreDrafts: boolean
  onLoadMoreDrafts: () => void
}) {
  const workspace = useGalleryStudioWorkspace(inputs)
  const slots = inputs.intent.slots
    .filter((slot) => slot.isActive)
    .toSorted((left, right) => (left.position ?? 999) - (right.position ?? 999))
  const mutationError =
    workspace.actions.savePlan.error ??
    workspace.actions.preview.error ??
    workspace.actions.launch.error ??
    workspace.actions.cancel.error ??
    workspace.actions.generateAnchor.error ??
    workspace.actions.approveAnchor.error ??
    workspace.actions.selectFamilyMaster.error

  return (
    <div className="space-y-5">
      <WorkflowSteps step={workspace.step} />

      <WorkspaceMutationError error={mutationError} />

      <IdentityReadiness
        inputs={inputs}
        isGenerating={workspace.actions.generateAnchor.isPending}
        isApproving={workspace.actions.approveAnchor.isPending}
        onGenerate={(contractId) => workspace.actions.generateAnchor.mutate(contractId)}
        onApprove={(contractId, attemptId) =>
          workspace.actions.approveAnchor.mutate({ contractId, attemptId })
        }
      />

      {workspace.step === "plan" ? (
        <div className="grid items-start gap-5 xl:grid-cols-[240px_minmax(0,1fr)]">
          <SlotAnchors
            slots={slots}
            editors={workspace.editors}
            drafts={inputs.existingDrafts}
            planInputs={inputs}
            activeSlotId={workspace.activeSlotId}
            onActivate={workspace.activateSlot}
          />

          <div className="min-w-0 space-y-4">
            {slots.map((slot) => (
              <GalleryStudioSlotSection
                key={slot.id}
                configSku={inputs.configSku}
                slot={slot}
                editor={workspace.editors[slot.id] ?? null}
                active={workspace.activeSlotId === slot.id}
                planInputs={inputs}
                drafts={inputs.existingDrafts}
                onActivate={() => workspace.activateSlot(slot.id)}
                onChange={workspace.updateEditor}
              />
            ))}

            {hasMoreDrafts ? (
              <div className="flex justify-center">
                <Button onClick={onLoadMoreDrafts} disabled={isLoadingMoreDrafts}>
                  {isLoadingMoreDrafts ? (
                    <LoaderCircle className="size-4 animate-spin" />
                  ) : (
                    <Images className="size-4" />
                  )}
                  Load later draft identities
                </Button>
              </div>
            ) : null}

            <SecondarySlotSetup
              isEmpty={workspace.secondarySlots.length === 0}
              error={workspace.actions.updateSlots.error}
              readbackError={workspace.actions.rereadSlotPlan.error}
              isPending={
                workspace.actions.updateSlots.isPending ||
                workspace.actions.rereadSlotPlan.isPending
              }
              hasPendingSlotReadback={workspace.hasPendingSlotReadback}
              onAdd={workspace.addSecondarySlot}
              onReread={workspace.rereadSlotPlan}
            />

            {workspace.validationError ? (
              <p role="alert" className="text-sm text-destructive">
                {workspace.validationError}
              </p>
            ) : null}

            <div className="flex justify-end">
              <Button
                variant="primary"
                onClick={() => void workspace.reviewGeneration()}
                disabled={
                  workspace.secondarySlots.length === 0 || workspace.actions.savePlan.isPending
                }
              >
                {workspace.actions.savePlan.isPending ? (
                  <LoaderCircle className="size-4 animate-spin" />
                ) : (
                  <FlaskConical className="size-4" />
                )}
                Review generation
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {workspace.step === "review" && workspace.preview ? (
        <GenerationReview
          preview={workspace.preview}
          regenerateItems={workspace.regenerateItems}
          isRefreshing={workspace.actions.preview.isPending}
          isLaunching={workspace.actions.launch.isPending}
          previewError={workspace.actions.preview.error}
          selectionMatches={workspace.previewSelectionMatches}
          onBack={() => workspace.setStep("plan")}
          onToggle={(identity) => void workspace.toggleRegeneration(identity)}
          onLaunch={() => void workspace.launchGeneration()}
        />
      ) : null}

      {workspace.step === "results" ? (
        <RunResults
          job={workspace.job.data ?? null}
          inputs={inputs}
          isLoading={workspace.job.isLoading}
          error={workspace.job.error}
          isCancelling={workspace.actions.cancel.isPending}
          hasMoreAnalysis={Boolean(workspace.job.hasNextPage)}
          isLoadingMoreAnalysis={workspace.job.isFetchingNextPage}
          onCancel={() => void workspace.cancelGeneration()}
          onLoadMoreAnalysis={() => void workspace.job.fetchNextPage()}
          onBack={() => workspace.setStep("plan")}
          isSelectingMaster={workspace.actions.selectFamilyMaster.isPending}
          isRejecting={workspace.actions.rejectScene.isPending}
          onSelectMaster={(selection) => workspace.actions.selectFamilyMaster.mutate(selection)}
          onReject={(rejection) => void workspace.rejectScene(rejection)}
          onRegenerate={(identity) => void workspace.reviewPaidRegeneration(identity)}
        />
      ) : null}
    </div>
  )
}

function SecondarySlotSetup({
  isEmpty,
  error,
  readbackError,
  isPending,
  hasPendingSlotReadback,
  onAdd,
  onReread,
}: {
  isEmpty: boolean
  error: Error | null
  readbackError: Error | null
  isPending: boolean
  hasPendingSlotReadback: boolean
  onAdd: (name: string) => Promise<void>
  onReread: () => Promise<void>
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [name, setName] = useState("")
  const [validationError, setValidationError] = useState<string | null>(null)
  const conflict = slotPlanConflictVersion(error)
  const requiresReread = conflict !== null || hasPendingSlotReadback

  const submit = async () => {
    const parsedName = galleryStudioSlotNameSchema.safeParse(name)
    if (!parsedName.success) {
      setValidationError(
        name.trim().length === 0 ? "Enter a Slot name." : "Use 160 characters or fewer."
      )
      return
    }
    setValidationError(null)
    try {
      await onAdd(parsedName.data)
    } catch {
      return
    }
    setName("")
    setIsOpen(false)
  }

  const reread = async () => {
    try {
      await onReread()
    } catch {
      return
    }
    if (hasPendingSlotReadback) {
      setName("")
      setIsOpen(false)
    }
  }

  if (!isOpen) {
    if (!isEmpty) {
      return (
        <div>
          <Button size="sm" onClick={() => setIsOpen(true)}>
            <Plus className="size-4" /> Add secondary Slot
          </Button>
        </div>
      )
    }
    return (
      <Callout
        tone="info"
        title="No secondary Slots"
        action={
          <Button size="sm" onClick={() => setIsOpen(true)}>
            <Plus className="size-4" /> Add secondary Slot
          </Button>
        }
      >
        <p className="mt-2 text-sm text-muted-foreground">
          Name the first Slot, then complete its semantic planning fields below.
        </p>
      </Callout>
    )
  }

  return (
    <form
      className="rounded-xl border border-border bg-card p-4 shadow-card"
      onSubmit={(event) => {
        event.preventDefault()
        void submit()
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold">Add secondary Slot</h3>

          <p className="mt-1 text-xs text-muted-foreground">
            This changes only the ordered Slot list. It starts no generation work.
          </p>
        </div>

        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={() => setIsOpen(false)}
          disabled={isPending || requiresReread}
        >
          Cancel
        </Button>
      </div>

      <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end">
        <FormField
          id="gallery-secondary-slot-name"
          label="Secondary Slot name"
          error={validationError}
          className="flex-1"
        >
          {(props) => (
            <TextInput
              {...props}
              id="gallery-secondary-slot-name"
              value={name}
              maxLength={160}
              hasError={Boolean(validationError)}
              disabled={isPending}
              onChange={(event) => setName(event.target.value)}
            />
          )}
        </FormField>

        <Button type="submit" variant="primary" disabled={isPending || requiresReread}>
          {isPending ? (
            <LoaderCircle className="size-4 animate-spin" />
          ) : (
            <Plus className="size-4" />
          )}
          Add Slot
        </Button>
      </div>

      {requiresReread ? (
        <div
          role="alert"
          className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-lg bg-warning/10 px-3 py-2"
        >
          <p className="text-xs text-warning">
            {conflict !== null
              ? `The Slot list is now at version ${conflict}.`
              : "The Slot was saved, but its authoritative readback has not succeeded."}
          </p>

          <Button type="button" size="sm" onClick={() => void reread()} disabled={isPending}>
            Reread Slot list
          </Button>
        </div>
      ) : null}

      {conflict === null && error ? (
        <p role="alert" className="mt-3 text-xs text-destructive">
          {error.message}
        </p>
      ) : null}

      {readbackError ? (
        <p role="alert" className="mt-3 text-xs text-destructive">
          {readbackError.message}
        </p>
      ) : null}
    </form>
  )
}

function slotPlanConflictVersion(error: Error | null): number | null {
  if (!(error instanceof GalleryStudioApiError) || error.kind !== "conflict") {
    return null
  }
  const details = galleryStudioSlotPlanConflictDetailsSchema.safeParse(error.details)
  return details.success ? details.data.currentVersion : null
}

function IdentityReadiness({
  inputs,
  isGenerating,
  isApproving,
  onGenerate,
  onApprove,
}: {
  inputs: GalleryStudioPlanInputs
  isGenerating: boolean
  isApproving: boolean
  onGenerate: (contractId: string) => void
  onApprove: (contractId: string, attemptId: string) => void
}) {
  return (
    <section className="rounded-xl border border-border bg-card p-4 shadow-card">
      <h3 className="text-sm font-semibold">Exact product identity</h3>

      <p className="mt-1 text-xs text-muted-foreground">
        Compare each source with its immutable anchor. Scene generation stays blocked until
        approval.
      </p>

      <div className="mt-3 grid gap-3 lg:grid-cols-2">
        {inputs.currentIdentityContracts.map((contract) => {
          const attempts = inputs.currentIdentityAnchorAttempts.filter(
            (attempt) => attempt.identityContractId === contract.id
          )
          const latest = attempts[0]
          const approved = attempts.find(
            (attempt) => attempt.id === contract.approvedAnchorAttemptId
          )
          const displayed = approved ?? latest
          return (
            <article key={contract.id} className="rounded-lg border border-border/70 p-3">
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-medium">{contract.simpleSku}</p>

                <StatusBadge
                  size="sm"
                  tone={contract.status === "approved" ? "success" : "warning"}
                >
                  {contract.status}
                </StatusBadge>
              </div>

              <div className="mt-3 grid grid-cols-2 gap-3">
                <IdentityImage label="Exact source" url={contract.sourceUrl} />

                <IdentityImage label="Identity anchor" url={displayed?.url ?? null} />
              </div>

              <div className="mt-3 flex justify-end">
                <IdentityAnchorAction
                  contract={contract}
                  latest={latest}
                  isGenerating={isGenerating}
                  isApproving={isApproving}
                  onGenerate={onGenerate}
                  onApprove={onApprove}
                />
              </div>
            </article>
          )
        })}
      </div>

      {inputs.currentFamilyMasters.length > 0 ? (
        <p className="mt-3 text-xs text-muted-foreground">
          {`${inputs.currentFamilyMasters.length} approved family masters. Selection pins the reviewed Candidate and clean scene plate.`}
        </p>
      ) : null}
    </section>
  )
}

function IdentityAnchorAction({
  contract,
  latest,
  isGenerating,
  isApproving,
  onGenerate,
  onApprove,
}: {
  contract: GalleryStudioPlanInputs["currentIdentityContracts"][number]
  latest: GalleryStudioPlanInputs["currentIdentityAnchorAttempts"][number] | undefined
  isGenerating: boolean
  isApproving: boolean
  onGenerate: (contractId: string) => void
  onApprove: (contractId: string, attemptId: string) => void
}) {
  if (latest?.status === "completed") {
    return (
      <Button
        size="sm"
        variant="primary"
        disabled={isApproving}
        onClick={() => onApprove(contract.id, latest.id)}
      >
        Approve anchor
      </Button>
    )
  }
  if (contract.status === "approved" || ["queued", "running"].includes(latest?.status ?? "")) {
    return null
  }
  return (
    <Button size="sm" disabled={isGenerating} onClick={() => onGenerate(contract.id)}>
      Generate paid anchor
    </Button>
  )
}

function IdentityImage({ label, url }: { label: string; url: string | null }) {
  return (
    <figure>
      <div className="aspect-square overflow-hidden rounded-md border border-border/70 bg-muted">
        {url ? <img src={url} alt={label} className="size-full object-contain" /> : null}
      </div>

      <figcaption className="mt-1 text-[11px] text-muted-foreground">{label}</figcaption>
    </figure>
  )
}

function WorkspaceMutationError({ error }: { error: Error | null }) {
  if (!error) {
    return null
  }
  return (
    <Callout role="alert" tone="destructive" dense title="Gallery Studio action failed">
      <p className="mt-1 text-xs text-destructive">{error.message}</p>
    </Callout>
  )
}

function SlotAnchors({
  slots,
  editors,
  drafts,
  planInputs,
  activeSlotId,
  onActivate,
}: {
  slots: GalleryStudioPlanInputs["intent"]["slots"]
  editors: Record<string, GalleryStudioPlanSlot>
  drafts: GalleryStudioPlanInputs["existingDrafts"]
  planInputs: GalleryStudioPlanInputs
  activeSlotId: string
  onActivate: (slotId: string) => void
}) {
  return (
    <nav
      aria-label="Gallery Slots"
      className="sticky top-4 z-10 overflow-x-auto rounded-xl border border-border bg-card p-2 shadow-card xl:max-h-[calc(100vh-2rem)]"
    >
      <ol className="flex gap-1 xl:flex-col">
        {slots.map((slot, index) => {
          const active = slot.id === activeSlotId
          const editor = editors[slot.id]
          const ready =
            slot.kind === "main" ||
            Boolean(editor && isGalleryStudioPlanSlotReady(editor, planInputs))
          const draftCount = drafts.filter((draft) => draft.slotId === slot.id).length
          return (
            <li key={slot.id} className="shrink-0 xl:shrink">
              <a
                href={`#gallery-slot-${slot.id}`}
                aria-current={active ? "location" : undefined}
                onClick={() => onActivate(slot.id)}
                className={cn(
                  "flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-[13px] outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring/30",
                  active
                    ? "bg-muted font-medium text-foreground"
                    : "text-muted-foreground hover:bg-muted/40 hover:text-foreground"
                )}
              >
                {slot.kind === "main" ? (
                  <LockKeyhole className="size-3.5" />
                ) : (
                  <Square className="size-3.5" />
                )}

                <span className="min-w-0 flex-1">
                  <span className="block truncate">{`${index + 1}. ${slot.name}`}</span>

                  <span className="block truncate text-[11px] text-muted-foreground">
                    {slot.kind === "main"
                      ? "Fixed source"
                      : `${ready ? "Ready" : "Needs setup"} · ${draftCount} drafts`}
                  </span>
                </span>
              </a>
            </li>
          )
        })}
      </ol>
    </nav>
  )
}

function WorkflowSteps({ step }: { step: "plan" | "review" | "results" }) {
  const steps = [
    { id: "plan", label: "Plan" },
    { id: "review", label: "Review generation" },
    { id: "results", label: "Run results" },
  ] as const
  const activeIndex = steps.findIndex((item) => item.id === step)
  return (
    <ol className="flex flex-wrap items-center gap-2" aria-label="Gallery generation workflow">
      {steps.map((item, index) => (
        <li key={item.id} className="flex items-center gap-2">
          {index > 0 ? <span aria-hidden="true" className="h-px w-5 bg-border" /> : null}

          <StatusBadge tone={workflowStepTone(index, activeIndex)}>
            {index < activeIndex ? <CheckCircle2 className="size-3.5" /> : null}

            {item.label}
          </StatusBadge>
        </li>
      ))}
    </ol>
  )
}

function GenerationReview({
  preview,
  regenerateItems,
  isRefreshing,
  isLaunching,
  previewError,
  selectionMatches,
  onBack,
  onToggle,
  onLaunch,
}: {
  preview: GalleryDraftPreview
  regenerateItems: GalleryDraftIdentity[]
  isRefreshing: boolean
  isLaunching: boolean
  previewError: Error | null
  selectionMatches: boolean
  onBack: () => void
  onToggle: (identity: GalleryDraftIdentity) => void
  onLaunch: () => void
}) {
  const executableCount =
    preview.paidImageCallCount + preview.analysisCallCount + preview.assemblyOnlyCount
  const canLaunch =
    !preview.exceedsPaidItemCap &&
    preview.missingPrerequisites.length === 0 &&
    executableCount > 0 &&
    !isRefreshing &&
    !previewError &&
    selectionMatches
  const launchLabel = generationLaunchLabel(preview)
  return (
    <div className="space-y-5">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <ReviewCount
          icon={CircleDollarSign}
          label="Paid image calls"
          value={preview.paidImageCallCount}
        />

        <ReviewCount icon={FlaskConical} label="Analysis calls" value={preview.analysisCallCount} />

        <ReviewCount icon={Layers3} label="Assembly only" value={preview.assemblyOnlyCount} />

        <ReviewCount
          icon={Images}
          label="Free existing skips"
          value={preview.skippedExistingDrafts}
        />
      </div>

      {preview.missingPrerequisites.length > 0 ? (
        <Callout
          tone="destructive"
          icon={<AlertCircle className="size-4" />}
          title="Resolve every blocked item before launch"
        >
          <ul className="mt-2 space-y-1 text-sm text-destructive">
            {preview.missingPrerequisites.map((blocker) => (
              <li key={`${blocker.itemKey}-${blocker.code}`}>{blocker.message}</li>
            ))}
          </ul>
        </Callout>
      ) : null}

      {preview.exceedsPaidItemCap ? (
        <Callout tone="destructive" title="Paid call cap exceeded">
          <p className="mt-2 text-sm text-destructive">
            One run may start at most 50 paid image calls.
          </p>
        </Callout>
      ) : null}

      {previewError || !selectionMatches ? (
        <Callout tone="destructive" title="Refresh the reviewed worklist">
          <p className="mt-2 text-sm text-destructive">
            The regeneration selection is not confirmed by the current preview. Launch is disabled.
          </p>
        </Callout>
      ) : null}

      <div className="overflow-hidden rounded-xl border border-border bg-card shadow-card">
        <div className="border-b border-border/70 px-4 py-3">
          <h3 className="text-[15px] font-semibold tracking-tight">Exact frozen worklist</h3>

          <p className="mt-1 text-xs text-muted-foreground">
            Preview and launch use this same plan version and regeneration selection.
          </p>
        </div>

        <ul className="divide-y divide-border/70">
          {preview.items.map((item) => {
            const identity = itemIdentity(item)
            const selected = regenerateItems.some((candidate) => sameIdentity(candidate, identity))
            return (
              <li
                key={item.itemKey}
                className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">
                    {`${item.slotName} · ${item.simpleSku}`}
                  </p>

                  <p className="mt-1 text-xs text-muted-foreground">
                    {`${item.marketplace} · ${item.format}`}
                  </p>

                  <ReviewedDraftPins item={item} />
                </div>

                <div className="flex items-center gap-3">
                  <StatusBadge size="sm" tone={previewTone(item.status)}>
                    {previewStatus(item)}
                  </StatusBadge>

                  {selected ||
                  item.skippedExistingDraft ||
                  item.retryAvailable ||
                  item.priorAttempted ? (
                    <label className="flex items-center gap-2 text-xs text-foreground">
                      <input
                        type="checkbox"
                        checked={selected}
                        disabled={isRefreshing}
                        onChange={() => onToggle(identity)}
                        className="size-4 accent-info"
                      />

                      <span>{regenerationLabel(item)}</span>
                    </label>
                  ) : null}
                </div>
              </li>
            )
          })}
        </ul>
      </div>

      <div className="flex flex-wrap justify-between gap-3">
        <Button onClick={onBack}>
          <ArrowLeft className="size-4" /> Back to plan
        </Button>

        <Button variant="primary" onClick={onLaunch} disabled={!canLaunch || isLaunching}>
          {isLaunching ? (
            <LoaderCircle className="size-4 animate-spin" />
          ) : (
            <Play className="size-4" />
          )}

          {launchLabel}
        </Button>
      </div>
    </div>
  )
}

function ReviewedDraftPins({ item }: { item: GalleryDraftPreview["items"][number] }) {
  const request = item.sceneRequest
  const copy = Object.entries(item.copy)
    .map(([name, value]) => `${name}: ${value}`)
    .join(" · ")
  const references = request?.referencePins.map((pin) => `${pin.id}@${pin.byteHash}`).join(", ")
  const compositionAssets = item.composition.assets
    .map((asset) => `${asset.id}@${asset.byteHash}`)
    .join(", ")
  return (
    <dl className="mt-3 grid gap-x-4 gap-y-1 text-[11px] text-muted-foreground sm:grid-cols-[8rem_minmax(0,1fr)]">
      <dt>Brief and copy</dt>

      <dd className="break-words">{`${item.brief} · ${copy}`}</dd>

      <dt>References</dt>

      <dd className="break-all">{references?.length ? references : "None"}</dd>

      <dt>Product identity</dt>

      <dd className="break-all">
        {item.identityContractId && item.anchorAttemptId
          ? `${item.identityContractId} · anchor ${item.anchorAttemptId}`
          : "Missing"}
      </dd>

      <dt>Scene role</dt>

      <dd className="break-all">
        {item.sceneRole === "master"
          ? "Family master candidate"
          : `Sibling · master ${item.familyMasterId ?? "missing"}`}
      </dd>

      <dt>Visual Direction</dt>

      <dd className="break-all">
        {`${item.composition.versionId ?? "Missing"} · ${compositionAssets || "no composition assets"}`}
      </dd>

      <dt>Provider</dt>

      <dd className="break-all">
        {request
          ? `${request.provider}/${request.model} · ${JSON.stringify(request.settings)}`
          : `${item.generationProvider}/${item.generationModel} · ${JSON.stringify(item.generationSettings)}`}
      </dd>

      <dt>Prompt</dt>

      <dd className="break-words">
        {request ? `${request.promptVersion} · ${request.compiledPrompt}` : "Missing"}
      </dd>

      <dt>Renderer and font</dt>

      <dd className="break-all">
        {`${item.renderer.identity}@${item.renderer.version} · ${item.font.identity}@${item.font.hash}`}
      </dd>

      <dt>Marketplace contract</dt>

      <dd className="break-all">{request ? JSON.stringify(request.targetContract) : "Missing"}</dd>

      <dt>Expected head</dt>

      <dd className="break-all">{item.expectedParentRevisionId ?? "new document"}</dd>

      <dt>Stage and cost</dt>

      <dd>{`${item.nextStage} · ${item.requiresPaidCall ? "paid image call" : "no paid image call"}`}</dd>
    </dl>
  )
}

function generationLaunchLabel(preview: GalleryDraftPreview): string {
  const analysisOnlyCount = preview.items.filter(
    (item) => item.status === "ready" && item.nextStage === "analysis"
  ).length
  const work = [
    preview.paidImageCallCount > 0 ? `${preview.paidImageCallCount} paid` : null,
    analysisOnlyCount > 0 ? `${analysisOnlyCount} analysis-only` : null,
    preview.assemblyOnlyCount > 0 ? `${preview.assemblyOnlyCount} assembly-only` : null,
  ].filter((part): part is string => part !== null)
  return `Launch ${work.join(" · ")}`
}

export function RunResults({
  job,
  inputs,
  isLoading,
  error,
  isCancelling,
  hasMoreAnalysis,
  isLoadingMoreAnalysis,
  onCancel,
  onLoadMoreAnalysis,
  onBack,
  isSelectingMaster,
  isRejecting,
  onSelectMaster,
  onReject,
  onRegenerate,
}: {
  job: GalleryDraftJob | null
  inputs: GalleryStudioPlanInputs
  isLoading: boolean
  error: Error | null
  isCancelling: boolean
  hasMoreAnalysis: boolean
  isLoadingMoreAnalysis: boolean
  onCancel: () => void
  onLoadMoreAnalysis: () => void
  onBack: () => void
  isSelectingMaster: boolean
  isRejecting: boolean
  onSelectMaster: (input: {
    slotId: string
    marketplace: string
    format: GalleryDraftJob["items"][number]["format"]
    simpleSku: string
    identityContractId: string
    anchorAttemptId: string
    sceneRequestId: string
    plateId: string
    candidateId: string
  }) => void
  onReject: (input: {
    plateId: string
    failureClass:
      | "required_text"
      | "wrong_identity"
      | "invented_content"
      | "master_leakage"
      | "scene_branding"
      | "composition"
      | "other"
    reason: string
  }) => void
  onRegenerate: (identity: GalleryDraftIdentity) => void
}) {
  if (isLoading && !job) {
    return <GalleryStudioLoading />
  }
  if (error) {
    return (
      <Callout tone="destructive" title="Run results failed">
        <p className="mt-2 text-sm text-destructive">{error.message}</p>
      </Callout>
    )
  }
  if (!job) {
    return null
  }
  const active = job.status === "queued" || job.status === "running" || job.status === "cancelling"
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-4 rounded-xl border border-border bg-card p-5 shadow-card">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-[15px] font-semibold tracking-tight">Draft job</h3>

            <StatusBadge tone={jobTone(job.status)}>{jobStatus(job.status)}</StatusBadge>
          </div>

          <p className="mt-2 text-xs text-muted-foreground">
            {`Plan v${job.planVersion} · ${job.outcome.paidCallsStarted} paid image calls started`}
          </p>
        </div>

        {active ? (
          <Button
            variant="destructive"
            onClick={onCancel}
            disabled={isCancelling || job.status === "cancelling"}
          >
            <StopCircle className="size-4" /> Stop before next paid call
          </Button>
        ) : null}
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <OutcomeCount label="Completed" value={job.outcome.completed} tone="success" />

        <OutcomeCount label="Failed" value={job.outcome.failed} tone="destructive" />

        <OutcomeCount
          label="Stopped before spend"
          value={job.outcome.stoppedBeforeSpend}
          tone="warning"
        />

        <OutcomeCount label="Skipped existing" value={job.outcome.skippedExisting} tone="muted" />
      </div>

      {job.outcome.completedBeforeCancel > 0 ? (
        <Callout tone="info" title="Completed before cancellation">
          <p className="mt-2 text-sm text-muted-foreground">
            {`${job.outcome.completedBeforeCancel} drafts finished before the stop request was recorded.`}
          </p>
        </Callout>
      ) : null}

      <div className="overflow-hidden rounded-xl border border-border bg-card shadow-card">
        <ul className="divide-y divide-border/70">
          {job.items.map((item) => {
            const frozen = job.frozenWorklist.find(
              (candidate) => candidate.itemKey === item.itemKey
            )
            const findings = item.analysisProviderEvidence.flatMap(
              (attempt) => attempt.normalizedResult?.fidelityFindings ?? []
            )
            const alreadyMaster = inputs.currentFamilyMasters.some(
              (master) => master.plateId === item.scenePlateId
            )
            const identityContractId = frozen?.identityContractId ?? null
            const anchorAttemptId = frozen?.anchorAttemptId ?? null
            const sceneRequestId = item.sceneRequestId
            const scenePlateId = item.scenePlateId
            const candidateId = item.candidateId
            return (
              <li
                key={item.id}
                className="flex flex-wrap items-start justify-between gap-3 px-4 py-3"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">
                    {`${item.simpleSku} · ${item.marketplace} · ${item.format}`}
                  </p>

                  <p className="mt-1 text-xs text-muted-foreground">
                    {item.failureMessage ?? item.stage}
                  </p>

                  {item.imageProviderEvidence ? (
                    <p className="mt-1 text-xs text-muted-foreground">
                      {`${item.imageEvidenceSource === "reused_request" ? "Reused image evidence" : "Current job image call"} · ${item.imageProviderEvidence.provider} · ${item.imageProviderEvidence.model} · ${item.imageProviderEvidence.providerRequestId ?? "request pending"}`}
                    </p>
                  ) : null}

                  {item.analysisProviderEvidence.map((attempt) => (
                    <p key={attempt.id} className="mt-1 text-xs text-muted-foreground">
                      {`Analysis ${attempt.attempt}: ${attempt.provider} · ${attempt.model} · ${attempt.status}`}
                    </p>
                  ))}

                  {findings.length > 0 ? (
                    <ul className="mt-2 space-y-1 text-xs text-destructive">
                      {findings.map((finding, index) => (
                        <li
                          key={`${finding.code}-${index}`}
                        >{`${finding.code}: ${finding.detail}`}</li>
                      ))}
                    </ul>
                  ) : null}

                  {item.reviewEvidence ? (
                    <SceneReviewEvidence
                      evidence={item.reviewEvidence}
                      candidateUrl={item.candidateUrl}
                    />
                  ) : null}

                  {item.status === "completed" &&
                  scenePlateId &&
                  item.reviewEvidence &&
                  !item.reviewEvidence.rejectionClass ? (
                    <SceneRejectionControls
                      plateId={scenePlateId}
                      disabled={isRejecting}
                      onReject={onReject}
                    />
                  ) : null}

                  {item.completedBeforeCancel ? (
                    <p className="mt-1 text-xs font-medium text-foreground">
                      Completed before cancellation
                    </p>
                  ) : null}

                  {item.candidateArtifactHash ? (
                    <p className="mt-1 font-mono text-[11px] text-muted-foreground">
                      {`Candidate ${item.candidateArtifactHash.slice(0, 12)} · Revision ${item.revisionHash?.slice(0, 12) ?? "unknown"}`}
                    </p>
                  ) : null}
                </div>

                <div className="flex items-center gap-2">
                  {(item.status === "failed" && item.requiresPaidCall) ||
                  item.reviewEvidence?.rejectionClass ? (
                    <Button
                      size="sm"
                      disabled={isSelectingMaster}
                      onClick={() => onRegenerate(itemIdentity(item))}
                    >
                      <CircleDollarSign className="size-4" /> Review paid Slot regeneration
                    </Button>
                  ) : null}

                  {frozen?.sceneRole === "master" &&
                  item.status === "completed" &&
                  candidateId &&
                  sceneRequestId &&
                  scenePlateId &&
                  identityContractId &&
                  anchorAttemptId &&
                  !alreadyMaster ? (
                    <Button
                      size="sm"
                      variant="primary"
                      disabled={isSelectingMaster}
                      onClick={() =>
                        onSelectMaster({
                          slotId: item.slotId,
                          marketplace: item.marketplace,
                          format: item.format,
                          simpleSku: item.simpleSku,
                          identityContractId,
                          anchorAttemptId,
                          sceneRequestId,
                          plateId: scenePlateId,
                          candidateId,
                        })
                      }
                    >
                      Select family master
                    </Button>
                  ) : null}

                  <StatusBadge size="sm" tone={itemTone(item.status)}>
                    {itemStatus(item.status)}
                  </StatusBadge>
                </div>
              </li>
            )
          })}
        </ul>

        {hasMoreAnalysis ? (
          <div className="border-t border-border/70 p-3 text-center">
            <Button onClick={onLoadMoreAnalysis} disabled={isLoadingMoreAnalysis}>
              {isLoadingMoreAnalysis ? <LoaderCircle className="size-4 animate-spin" /> : null}
              Load older analysis evidence
            </Button>
          </div>
        ) : null}
      </div>

      <Button onClick={onBack}>
        <ArrowLeft className="size-4" /> Back to plan
      </Button>
    </div>
  )
}

function SceneReviewEvidence({
  evidence,
  candidateUrl,
}: {
  evidence: NonNullable<GalleryDraftJob["items"][number]["reviewEvidence"]>
  candidateUrl: string | null
}) {
  return (
    <div className="mt-3 max-w-3xl">
      <div className="grid gap-3 sm:grid-cols-3">
        <IdentityImage label="Exact source" url={evidence.sourceUrl} />

        <SceneCropImage
          label={evidence.productCrop ? "Scene product crop" : "Generated scene"}
          url={evidence.sceneUrl}
          crop={evidence.productCrop}
        />

        <IdentityImage label="Rendered Candidate" url={candidateUrl} />
      </div>

      {evidence.rejectionClass || evidence.usabilityReasons.length > 0 ? (
        <p className="mt-2 text-xs text-destructive">
          {`Rejection: ${humanizeReason(evidence.rejectionClass ?? evidence.usabilityReasons[0] ?? "unspecified")} · ${evidence.usabilityReasons.map(humanizeReason).join(", ")}`}
        </p>
      ) : null}
    </div>
  )
}

type SceneRejectionClass =
  | "required_text"
  | "wrong_identity"
  | "invented_content"
  | "master_leakage"
  | "scene_branding"
  | "composition"
  | "other"

function SceneRejectionControls({
  plateId,
  disabled,
  onReject,
}: {
  plateId: string
  disabled: boolean
  onReject: (input: { plateId: string; failureClass: SceneRejectionClass; reason: string }) => void
}) {
  const [failureClass, setFailureClass] = useState<SceneRejectionClass>("wrong_identity")
  const [reason, setReason] = useState("")
  return (
    <div className="mt-3 grid gap-2 rounded-lg border border-border/70 p-3 sm:grid-cols-[180px_minmax(0,1fr)_auto]">
      <label className="grid gap-1 text-xs font-medium">
        Named rejection class
        <select
          className="h-9 rounded-md border border-input bg-background px-2 text-sm"
          value={failureClass}
          onChange={(event) => setFailureClass(event.target.value as SceneRejectionClass)}
        >
          <option value="required_text">Required text</option>

          <option value="wrong_identity">Wrong identity</option>

          <option value="invented_content">Invented content</option>

          <option value="master_leakage">Master leakage</option>

          <option value="scene_branding">Scene branding</option>

          <option value="composition">Composition</option>

          <option value="other">Other</option>
        </select>
      </label>

      <label className="grid gap-1 text-xs font-medium">
        Human review reason
        <input
          className="h-9 rounded-md border border-input bg-background px-3 text-sm"
          value={reason}
          maxLength={500}
          placeholder="Name the visible defect"
          onChange={(event) => setReason(event.target.value)}
        />
      </label>

      <Button
        size="sm"
        variant="destructive"
        className="self-end"
        disabled={disabled || reason.trim().length === 0}
        onClick={() => onReject({ plateId, failureClass, reason: reason.trim() })}
      >
        Reject scene
      </Button>
    </div>
  )
}

function SceneCropImage({
  label,
  url,
  crop,
}: {
  label: string
  url: string
  crop: NonNullable<GalleryDraftJob["items"][number]["reviewEvidence"]>["productCrop"]
}) {
  return (
    <figure>
      <div className="relative aspect-square overflow-hidden rounded-md border border-border/70 bg-muted">
        {crop ? (
          <img
            src={url}
            alt={label}
            className="absolute max-w-none"
            style={{
              width: `${100 / crop.width}%`,
              height: `${100 / crop.height}%`,
              left: `${(-crop.x / crop.width) * 100}%`,
              top: `${(-crop.y / crop.height) * 100}%`,
            }}
          />
        ) : (
          <img src={url} alt={label} className="size-full object-contain" />
        )}
      </div>

      <figcaption className="mt-1 text-[11px] text-muted-foreground">{label}</figcaption>
    </figure>
  )
}

function humanizeReason(reason: string): string {
  return reason.replaceAll("_", " ").replace(/^./, (value) => value.toUpperCase())
}

function ReviewCount({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Images
  label: string
  value: number
}) {
  return (
    <div className="rounded-xl border border-border bg-card p-4 shadow-card">
      <Icon className="size-4 text-muted-foreground" />

      <p className="mt-3 text-2xl font-semibold tracking-tight tabular-nums">{value}</p>

      <p className="mt-1 text-xs text-muted-foreground">{label}</p>
    </div>
  )
}

function OutcomeCount({ label, value, tone }: { label: string; value: number; tone: BadgeTone }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4 shadow-card">
      <StatusBadge size="sm" tone={tone}>
        {label}
      </StatusBadge>

      <p className="mt-3 text-2xl font-semibold tracking-tight tabular-nums">{value}</p>
    </div>
  )
}

function itemIdentity(item: GalleryDraftIdentity): GalleryDraftIdentity {
  return {
    slotId: item.slotId,
    simpleSku: item.simpleSku,
    marketplace: item.marketplace,
    format: item.format,
  }
}

function sameIdentity(left: GalleryDraftIdentity, right: GalleryDraftIdentity): boolean {
  return (
    left.slotId === right.slotId &&
    left.simpleSku === right.simpleSku &&
    left.marketplace === right.marketplace &&
    left.format === right.format
  )
}

function previewTone(status: GalleryDraftPreview["items"][number]["status"]): BadgeTone {
  if (status === "ready") {
    return "info"
  }
  if (status === "blocked") {
    return "destructive"
  }
  return "muted"
}

function previewStatus(item: GalleryDraftPreview["items"][number]): string {
  if (item.status === "ready") {
    if (item.nextStage === "analysis") {
      return "Ready for stored-scene analysis"
    }
    if (item.nextStage === "assembly") {
      return "Ready for assembly"
    }
    return "Ready for paid image and analysis"
  }
  if (item.status === "blocked") {
    return "Blocked"
  }
  return "Skip existing"
}

function regenerationLabel(item: GalleryDraftPreview["items"][number]): string {
  if (item.retryAvailable) {
    return "Reuse stored scene"
  }
  if (item.priorAttempted) {
    return "Rerun paid image"
  }
  return "Regenerate"
}

function jobTone(status: GalleryDraftJob["status"]): BadgeTone {
  if (status === "completed") {
    return "success"
  }
  if (status === "partial" || status === "cancelling") {
    return "warning"
  }
  if (status === "failed" || status === "cancelled") {
    return "destructive"
  }
  return "info"
}

function jobStatus(status: GalleryDraftJob["status"]): string {
  return status.replaceAll("_", " ").replace(/^./, (value) => value.toUpperCase())
}

function itemTone(status: GalleryDraftJob["items"][number]["status"]): BadgeTone {
  if (status === "completed") {
    return "success"
  }
  if (status === "failed") {
    return "destructive"
  }
  if (status === "stopped_before_spend") {
    return "warning"
  }
  if (status === "queued" || status === "running") {
    return "info"
  }
  return "muted"
}

function itemStatus(status: GalleryDraftJob["items"][number]["status"]): string {
  return status.replaceAll("_", " ").replace(/^./, (value) => value.toUpperCase())
}

function workflowStepTone(index: number, activeIndex: number): BadgeTone {
  if (index === activeIndex) {
    return "info"
  }
  return index < activeIndex ? "success" : "muted"
}
